import React, { useState, useRef, useEffect } from 'react';
import { View, Heading, Text, colors, BasicChip } from '@fairygodboss/ui';
import { labelToTextMap } from '../adviceHashMap';
import { isOverflown } from '../../../Events/EventLandingPage/components/helpers';
import VisibilityPixel from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import { useAdvicePage } from '../AdvicePageContext';

export const FeaturedCommunityDiscussionCard = ({
    content = {},
    fixedHeight,
    maxTitleLine,
    maxBodyLine,
    alignTop,
}) => {
    const [hasOverflow, setHasOverflow] = useState(true);
    const [maxBodyLines, setMaxBodyLines] = useState(maxBodyLine);
    const [maxTitleLines, setMaxTitleLines] = useState(maxTitleLine);

    const { onDiscussionLinkClick } = useAdvicePage();

    useEffect(() => {
        if (fixedHeight) {
            return;
        }
        if (maxTitleLines > 2 && maxBodyLines > 1) {
            setMaxBodyLines(1);
        }
    }, [maxBodyLines, maxTitleLines, fixedHeight]);

    const containerRef = useRef();
    const bodyRef = useRef();
    const titleRef = useRef();
    const {
        commentCount,
        likes,
        contentUrl,
        title,
        description,
        topic,
        hash,
    } = content;

    const impressionMetrics = {
        misc_event_type: 'advice-hub-impression',
        community_content_key: hash,
        community_content_location: 'advice-content',
        impression: 1,
        // TODO: if we have sponsored company, add company ID
        company_id: [null],
        snowPlow: {
            component: 'tile',
            tileType: 'article',
            position: 'main',
            pageType: 'collection',
            pageSection: 'advice_landing',
        },
    };

    const label = topic?.label;
    const commentText = commentCount === 1 ? 'Comment' : 'Comments';
    const likeText = likes === 1 ? 'Like' : 'Likes';

    const getLineHeight = (el, lineHeight) => {
        const divHeight = el?.offsetHeight;
        const lines = parseInt(divHeight / lineHeight, 10);
        return lines;
    };

    useEffect(() => {
        if (fixedHeight) {
            setMaxBodyLines(maxBodyLine || 2);
            return;
        }
        if (bodyRef?.current && hasOverflow) {
            const lines = getLineHeight(bodyRef?.current, 26);
            if (lines > 2) {
                setMaxBodyLines(2);
            }
        }
    }, [bodyRef, hasOverflow, fixedHeight, maxBodyLine]);

    useEffect(() => {
        if (fixedHeight) {
            setMaxTitleLines(maxTitleLine || 2);
            return;
        }
        if (titleRef?.current && hasOverflow) {
            const lines = getLineHeight(titleRef?.current, 40);
            if (lines > 3) {
                setMaxTitleLines(3);
            }
        }
    }, [titleRef, hasOverflow, fixedHeight, maxTitleLine]);

    useEffect(() => {
        if (fixedHeight) {
            return;
        }
        if (containerRef?.current) {
            setHasOverflow(isOverflown(containerRef?.current));
        }
    }, [containerRef, fixedHeight]);

    return content.hash ? (
        <View
            height={fixedHeight || 'auto'}
            display="flex"
            as="a"
            href={contentUrl}
            target="_self"
            width="100%"
            alignItems={alignTop ? 'flex-start' : 'flex-end'}
            onClick={(event) =>
                onDiscussionLinkClick({ event, hash, contentUrl })
            }
            flexGrow="1"
            mt={{ _: 'inherit', xs: '23px', sm: '23px', md: '0px' }}
        >
            <VisibilityPixel metrics={impressionMetrics} />
            <View
                width={{ _: '100%', md: 'inherit' }}
                maxHeight={{ _: 'inherit', md: '95%' }}
                position="relative"
                height="100%"
            >
                <View
                    px="16px"
                    pt="16px"
                    pb="10px"
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    css={{
                        ':hover': {
                            boxShadow: '0px 24px 40px rgba(53, 53, 53, 0.16)',
                        },
                    }}
                    borderRadius="8px"
                    border={`1px solid ${colors.neutral[20]}`}
                    backgroundColor="#FFFFFF"
                    height={{
                        _: '100%',
                        sm: fixedHeight || '100%',
                        md: '100%',
                    }}
                    ref={containerRef}
                >
                    <View
                        maxHeight={{ _: 'inherit', md: '174px' }}
                        overflow="hidden"
                        borderRadius="0 0 8px 8px"
                        css={{
                            span: {
                                ':hover': {
                                    textDecoration: 'underline',
                                    color: colors.neutral[100],
                                },
                            },
                        }}
                    >
                        <View
                            css={{
                                span: {
                                    borderWidth: '0px',
                                    backgroundColor: colors.secondary[10],
                                    color: colors.secondary[70],
                                    fontWeight: '600',
                                },
                            }}
                        >
                            <BasicChip
                                size="medium"
                                text={labelToTextMap[label]}
                                variant="primary"
                                mb="8px"
                            />
                        </View>
                        <View ref={titleRef} overflow="hidden">
                            <Heading
                                variant={{
                                    _: 'headingXs',
                                    sm: 'headingSm',
                                }}
                                color={colors.neutral[100]}
                                css={{
                                    ...(maxTitleLines
                                        ? {
                                              overflow: 'hidden',
                                              display: '-webkit-box',
                                              '-webkit-line-clamp': `${maxTitleLines}`,
                                              '-webkit-box-orient': 'vertical',
                                          }
                                        : null),
                                }}
                            >
                                {title}
                            </Heading>
                        </View>
                        <View
                            ref={bodyRef}
                            overflow="hidden"
                            display={{
                                _: 'inherit',
                                md: 'none',
                                lg: 'inherit',
                            }}
                            color={colors.neutral[100]}
                            paddingBottom={maxBodyLine ? '12px' : null}
                        >
                            <Text
                                pt="12px"
                                variant="bodyMd"
                                css={{
                                    ...(maxBodyLines
                                        ? {
                                              overflow: 'hidden',
                                              display: '-webkit-box',
                                              '-webkit-line-clamp': `${maxBodyLines}`,
                                              '-webkit-box-orient': 'vertical',
                                          }
                                        : null),
                                }}
                            >
                                {description.substring(0, 300)}
                            </Text>
                        </View>
                    </View>
                    <View
                        css={{
                            span: {
                                ':hover': {
                                    textDecoration: 'underline',
                                    color: colors.neutral[100],
                                },
                            },
                        }}
                        color={colors.neutral[80]}
                    >
                        <Text
                            pt="8px"
                            as="span"
                            margin="0"
                            textAlign="left"
                            variant="labelMd"
                        >
                            {`${likes} ${likeText}`}
                        </Text>
                        {'   •  '}
                        <Text
                            pt="8px"
                            as="span"
                            margin="0"
                            textAlign="left"
                            variant="labelMd"
                        >
                            {`${commentCount} ${commentText}`}
                        </Text>
                    </View>
                </View>
            </View>
        </View>
    ) : null;
};

FeaturedCommunityDiscussionCard.displayName = 'FeaturedCommunityDiscussionCard';
